import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ImageUpload } from "./ImageUpload";

const EMPTY = { image: "", caption_en: "", caption_hi: "", location: "", visible: true };
const FIELDS = [["location", "Location (e.g. Bhaiyathan, Surajpur)"], ["caption_en", "Caption (English)"], ["caption_hi", "Caption (Hindi)"]];
const inputCls = "mt-1 h-10 w-full rounded-lg border border-white/10 bg-ink px-3 text-sm outline-none focus:border-saffron";

export const GalleryPanel = () => {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);

  useEffect(() => {
    api.get("/admin/gallery").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
  }, []);

  const save = async (item) => {
    if (!item.image) {
      toast.error("Please upload a photo first");
      return;
    }
    try {
      const body = { image: item.image, caption_en: item.caption_en, caption_hi: item.caption_hi, location: item.location, visible: item.visible };
      const { data } = item.id ? await api.put(`/admin/gallery/${item.id}`, body) : await api.post("/admin/gallery", body);
      setList((p) => (item.id ? p.map((x) => (x.id === data.id ? data : x)) : [data, ...p]));
      setEdit(null);
      toast.success("Saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this photo from the gallery?")) return;
    await api.delete(`/admin/gallery/${id}`).catch(() => {});
    setList((p) => p.filter((x) => x.id !== id));
  };

  return (
    <div data-testid="admin-gallery">
      <button className="btn-primary" onClick={() => setEdit({ ...EMPTY })} data-testid="gallery-add-btn"><Plus size={16} /> Add photo</button>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {list.map((g) => (
          <div key={g.id} className="bento overflow-hidden p-0" data-testid={`admin-gallery-${g.id}`}>
            <div className="aspect-[4/3] overflow-hidden"><img src={g.image} alt={g.caption_en || g.location} className="h-full w-full object-cover" /></div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{g.caption_en || "\u2014"}</p>
                  <p className="truncate text-xs text-slate-400">{g.location}</p>
                </div>
                <Switch checked={g.visible} onCheckedChange={(v) => save({ ...g, visible: v })} data-testid={`gallery-visible-${g.id}`} />
              </div>
              <div className="mt-3 flex gap-2">
                <button className="btn-ghost px-3 py-1.5 text-xs" onClick={() => setEdit(g)} data-testid={`gallery-edit-${g.id}`}><Pencil size={13} /> Edit</button>
                <button className="btn-ghost px-3 py-1.5 text-xs text-red-300" onClick={() => remove(g.id)} data-testid={`gallery-delete-${g.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!list.length && <p className="mt-6 text-sm text-slate-400" data-testid="gallery-empty">No photos yet. The gallery stays hidden on the website until you add one.</p>}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-card text-white">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} gallery photo</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <ImageUpload value={edit.image} onChange={(url) => setEdit({ ...edit, image: url })} testid="gallery-photo" label="Shop photo" />
              {FIELDS.map(([k, l]) => (
                <label key={k} className="block"><span className="text-xs uppercase tracking-wider text-slate-400">{l}</span>
                  <input maxLength={k === "location" ? 80 : 120} className={inputCls} value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`gallery-input-${k}`} />
                </label>
              ))}
              <button className="btn-primary w-full justify-center" onClick={() => save(edit)} data-testid="gallery-save-btn">Save</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
